import React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useFeature } from 'flagged';
import MenuButton from '@/components/common/MenuButton';
import OpportunityClosedState from '@/constants/OpportunityClosedState';
import FeatureFlags from '@/constants/system/FeatureFlags';
import CreateEditDialogType from '@/constants/ui/CreateEditDialogType';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import type Opportunity from '@/types/api/Opportunity';
import { showErrorMessage } from '@/utils/ToastFeedback';
import showSuccessMessage from '@/utils/toasts/showSuccessMessage';
import useOpportunityServices from '../hooks/useOpportunityServices';
import type OpportunitiesDataListRow from '../types/OpportunitiesDataListRow';

const MENU_CLASSES = {
  list: 'py-0',
};

const MENU_ITEM_CLASSES = {
  root: 'p-3',
};

type OpportunityMenuProps = {
  onArchive?: () => void;
  onDelete?: () => void;
  opportunity: Opportunity | OpportunitiesDataListRow | null;
};

export default function OpportunityMenu({
  onArchive,
  onDelete,
  opportunity,
}: OpportunityMenuProps) {
  const hasLinking = useFeature(FeatureFlags.OPPORTUNITIES_LINKING);
  const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
  const open = Boolean(anchorEl);
  const { opportunityId } = opportunity || {};
  const { openDialog, closeDialog } = useDialogManager();
  const { archiveOpportunity, deleteOpportunity, unarchiveOpportunity } =
    useOpportunityServices();

  const handleOpen = (event: React.MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const isWon = opportunity?.closedState === OpportunityClosedState.WON;

  const handleMenuClose = (event: Event | React.SyntheticEvent) => {
    event.stopPropagation();
    handleClose();
  };

  const openEditOpportunityDialog = React.useCallback(
    (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      event.stopPropagation();
      handleClose();

      openDialog(Dialogs.CREATE_EDIT_OPPORTUNITY, {
        onClose: () => {},
        open: true,
        opportunity,
        type: CreateEditDialogType.EDIT,
      });
    },
    [opportunity, openDialog],
  );

  const handleLink = React.useCallback(
    (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      event.stopPropagation();
      handleClose();
      openDialog(Dialogs.LINK_TO_OPPORTUNITY, {
        opportunityId,
        onClose: closeDialog,
      });
    },
    [opportunityId, openDialog],
  );

  const archive = React.useCallback(
    async (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      event.stopPropagation();
      if (opportunity) {
        try {
          await archiveOpportunity(opportunity.opportunityId);
          showSuccessMessage({
            message: translate('OPPORTUNITIES_ARCHIVE_SUCCESS'),
          });
          if (onArchive) {
            onArchive();
          }
        } catch (e) {
          showErrorMessage({
            message: translate('OPPORTUNITIES_ARCHIVE_FAILURE'),
          });
        }
      }

      handleClose();
    },
    [archiveOpportunity, onArchive, opportunity],
  );

  const unarchive = React.useCallback(
    async (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      event.stopPropagation();
      if (opportunity) {
        try {
          await unarchiveOpportunity(opportunity.opportunityId);
          showSuccessMessage({
            message: translate('OPPORTUNITIES_UNARCHIVE_SUCCESS'),
          });
          if (onArchive) {
            onArchive();
          }
        } catch (e) {
          showErrorMessage({
            message: translate('OPPORTUNITIES_UNARCHIVE_FAILURE'),
          });
        }
      }
      handleClose();
    },
    [unarchiveOpportunity, onArchive, opportunity],
  );

  const confirmDeleteOpportunityDialog = React.useCallback(
    (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      event.stopPropagation();
      handleClose();

      if (opportunity?.opportunityId) {
        openDialog(Dialogs.CONFIRMATION, {
          children: translate('OPPORTUNITIES_DELETE_CONFIRM_MSG'),
          confirmLabel: translate('OPPORTUNITIES_DELETE_OPPORTUNITY'),
          onCancel: closeDialog,
          onClose: closeDialog,
          onConfirm: async () => {
            try {
              await deleteOpportunity(opportunity.opportunityId);
              showSuccessMessage(translate('OPPORTUNITIES_DELETE_SUCCESS'));
              closeDialog();

              if (onDelete) {
                onDelete();
              }
            } catch (e) {
              showErrorMessage({
                message: translate('OPPORTUNITIES_DELETE_FAILURE'),
              });
            }
          },
        });
      }
    },
    [deleteOpportunity, opportunity],
  );

  if (!opportunity) return null;

  return (
    <>
      <MenuButton
        id={`opportunities-list-${opportunityId}-menu-button`}
        data-testid={`opportunities-list-${opportunityId}-menu-button`}
        aria-controls={
          open ? `opportunities-list-${opportunityId}-menu` : undefined
        }
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
      />
      <Menu
        id={`opportunities-list-${opportunityId}-menu`}
        anchorEl={anchorEl}
        classes={MENU_CLASSES}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': `opportunities-list-${opportunityId}-menu-button`,
        }}
      >
        <MenuItem
          classes={MENU_ITEM_CLASSES}
          data-testid={`opportunities-list-${opportunityId}-edit-button`}
          onClick={openEditOpportunityDialog}
        >
          {translate('EDIT')}
        </MenuItem>
        {hasLinking && isWon && (
          <MenuItem
            classes={MENU_ITEM_CLASSES}
            data-testid={`opportunities-list-${opportunityId}-link-button`}
            onClick={handleLink}
          >
            {translate('OPPORTUNITIES_LINK_CONTRIBUTION')}
          </MenuItem>
        )}
        {opportunity.archivedAt ? (
          <MenuItem
            classes={MENU_ITEM_CLASSES}
            data-testid={`opportunities-list-${opportunityId}-unarchive-button`}
            onClick={unarchive}
          >
            {translate('OPPORTUNITIES_UNARCHIVE')}
          </MenuItem>
        ) : (
          <MenuItem
            classes={MENU_ITEM_CLASSES}
            data-testid={`opportunities-list-${opportunityId}-archive-button`}
            onClick={archive}
          >
            {translate('OPPORTUNITIES_ARCHIVE')}
          </MenuItem>
        )}
        <MenuItem
          classes={MENU_ITEM_CLASSES}
          data-testid={`opportunities-list-${opportunityId}-delete-button`}
          onClick={confirmDeleteOpportunityDialog}
        >
          {translate('DELETE')}
        </MenuItem>
      </Menu>
    </>
  );
}
